import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Button,
	Card,
	Divider,
	Grid2,
	IconButton,
	InputAdornment,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { rollsetStatus } from "@/pages/rollshop/pages/rollsets/components/RollStatusInfo";
import { ReadyRollset } from "@/shared/models/Rolllset";
import { SectionSelector } from "../../selectors";
import { RollPassStatus } from "../RollPassStatus";
import { NewRollsetDialog } from "./NewRollsetDialog";

const formatLocationDiameter = (
	locationKey?: string | null,
	diameter?: number | null,
) => {
	const key = locationKey ?? null;
	const diam =
		diameter != null ? parseFloat(diameter.toFixed(3)) || null : null;

	if (key && diam) return `${key} | ${diam}`;
	if (diam) return `${diam}`;
	if (key) return key;
	return "";
};

type Props = {
	value: number | null;
	onChange: (value: number | null) => void;
	standId: number | null;
	standName: string | null;
	sectionId: number | null;
	show: boolean;
	onHide: (save: boolean) => void;
	onNewRollsetSaved?: (rollsetId: number) => void;
	rollsets: ReadyRollset[];
	isLoading: boolean;
};

const RollsetCard = ({
	rollset,
	isSelected,
	onClick,
}: {
	rollset: ReadyRollset;
	isSelected: boolean;
	onClick: () => void;
}) => {
	const status = rollsetStatus.find(
		(s) => s.code === rollset.RollsetStatusCode,
	);

	return (
		<Card
			data-test-id={`rollset-card-${rollset.SectionName}-${rollset.StandName}-${rollset.RollsetLetter}`}
			elevation={1}
			onClick={onClick}
			sx={{
				mb: 1,
				cursor: "pointer",
				borderRadius: "10px",
				border: isSelected ? "3px solid #1976d2" : "2px solid transparent",
				boxShadow: (theme) => (isSelected ? theme.shadows[4] : ""),
				transition: "all 0.2s ease-in-out",
				"&:hover": {
					boxShadow: (theme) => theme.shadows[4],
				},
				overflow: "hidden",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "stretch" }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						minWidth: 150,
						backgroundColor: status?.color,
						py: 1,
						px: 2,
					}}
				>
					<Typography fontWeight={800} fontSize="1.1rem" color="white">
						{rollset.StandName}
					</Typography>
					<Box
						sx={{
							backgroundColor: "rgba(255,255,255,0.25)",
							px: 1,
							py: 0.1,
							borderRadius: "10px",
							fontSize: "0.65rem",
							fontWeight: 700,
							mt: 0.3,
							color: "white",
						}}
					>
						{status?.name}
					</Box>
				</Box>

				<Box
					sx={{
						flex: 1,
						display: "flex",
						alignItems: "center",
						gap: 2,
						py: 1,
						px: 2,
						backgroundColor: "white",
					}}
				>
					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							display: "flex",
							alignItems: "center",
							gap: 1.5,
						}}
					>
						<Box sx={{ minWidth: "fit-content", flexShrink: 0 }}>
							<Typography
								noWrap
								sx={{
									fontSize: "0.6rem",
									opacity: 0.5,
									textTransform: "uppercase",
									letterSpacing: "0.05em",
									color: "text.secondary",
									whiteSpace: "nowrap",
								}}
							>
								▲ Top Roll
							</Typography>
							<Typography
								noWrap
								sx={{
									fontSize: "0.85rem",
									fontWeight: 700,
									color: "text.primary",
								}}
								data-test-id="top-roll-name"
							>
								{rollset.TopRollName}
							</Typography>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<Typography
									sx={{
										fontSize: "0.65rem",
										color: "text.secondary",
										whiteSpace: "nowrap",
									}}
								>
									{formatLocationDiameter(
										rollset.TopRollLocationKey,
										rollset.TopRollDiameter,
									)}
								</Typography>

								{rollset.TopRollPassesStatus && (
									<RollPassStatus
										passesStatus={rollset.TopRollPassesStatus}
										withLabel={false}
										justifyContent="flex-start"
									/>
								)}
							</Box>
						</Box>
					</Box>

					<Divider
						orientation="vertical"
						flexItem
						sx={{ borderColor: "rgba(0,0,0,0.1)" }}
					/>

					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							display: "flex",
							alignItems: "center",
							gap: 1.5,
						}}
					>
						<Box sx={{ minWidth: "fit-content", flexShrink: 0 }}>
							<Typography
								noWrap
								sx={{
									fontSize: "0.6rem",
									opacity: 0.5,
									textTransform: "uppercase",
									letterSpacing: "0.05em",
									color: "text.secondary",
									whiteSpace: "nowrap",
								}}
							>
								▲ Bottom Roll
							</Typography>
							<Typography
								noWrap
								sx={{
									fontSize: "0.85rem",
									fontWeight: 700,
									color: "text.primary",
								}}
								data-test-id="bottom-roll-name"
							>
								{rollset.BottomRollName}
							</Typography>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<Typography
									sx={{
										fontSize: "0.65rem",
										color: "text.secondary",
										whiteSpace: "nowrap",
									}}
								>
									{formatLocationDiameter(
										rollset.BottomRollLocationKey,
										rollset.BottomRollDiameter,
									)}
								</Typography>

								{rollset.BottomRollPassesStatus && (
									<RollPassStatus
										passesStatus={rollset.BottomRollPassesStatus}
										withLabel={false}
										justifyContent="flex-start"
									/>
								)}
							</Box>
						</Box>
					</Box>

					<Typography
						fontWeight={800}
						fontSize="1.3rem"
						sx={{
							opacity: 0.3,
							minWidth: 20,
							textAlign: "center",
							color: "text.primary",
						}}
					>
						{rollset.RollsetLetter}
					</Typography>
				</Box>
			</Box>
		</Card>
	);
};

export const RollsetModalSelector = ({
	value,
	onChange,
	standId,
	standName,
	sectionId,
	show,
	onHide,
	onNewRollsetSaved = () => {},
	rollsets,
	isLoading,
}: Props) => {
	const [filterValue, setFilterValue] = useState("");
	const [error, setError] = useState("");
	const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
	const [notificationMessage, setNotificationMessage] = useState<string>("");
	const [selectedRollsetId, setSelectedRollsetId] = useState<number | null>(
		value,
	);
	const [filteredRollsets, setFilteredRollsets] = useState<ReadyRollset[]>([]);
	const [showNewRollsetDialog, setShowNewRollsetDialog] = useState(false);

	const { setValue, handleSubmit } = useForm<ReadyRollset>({
		mode: "onChange",
	});

	const onSubmit: SubmitHandler<ReadyRollset> = async (data) => {
		const selectedRollset = rollsets.find((r) => r.RollsetId == data.RollsetId);

		if (
			(selectedRollset?.RollsetStatusCode == "S" ||
				selectedRollset?.RollsetStatusCode == "C" ||
				selectedRollset?.RollsetStatusCode == "A" ||
				selectedRollset?.RollsetStatusCode == "I") &&
			selectedRollset?.RollsetId != value
		) {
			setShowConfirmationDialog(true);
		} else {
			onChange(selectedRollsetId);
			onHide(true);
		}
	};

	useEffect(() => {
		if (!rollsets) {
			setFilteredRollsets([]);
			return;
		}

		if (!filterValue.trim()) {
			setFilteredRollsets(rollsets);
			return;
		}

		const filtered = rollsets.filter((rollset) =>
			Object.values(rollset).some((val) =>
				String(val).toLowerCase().includes(filterValue.toLowerCase()),
			),
		);
		setFilteredRollsets(filtered);
	}, [rollsets, filterValue]);

	useEffect(() => {
		if (show) {
			setSelectedRollsetId(value);
			setValue("RollsetId", value!);
		}
	}, [show, value]);

	// Get active rollset letters for the NewRollsetDialog
	const getActiveLetters = (): Set<string> => {
		const activeLetters = new Set<string>();
		rollsets.forEach((rollset) => {
			activeLetters.add(rollset.RollsetLetter);
		});
		return activeLetters;
	};

	return (
		<>
			<MesfModal
				open={show}
				handleClose={() => onHide(false)}
				title="SEARCH ROLLSETS"
				maxWidth="lg"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						dividers
						style={{ padding: "15px 50px", minWidth: "50vw" }}
					>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 4 }}>
								<SectionSelector label="Section" value={sectionId} disabled />
							</Grid2>
							<Grid2 size={{ xs: 12, md: 8 }}>
								<TextField
									fullWidth
									label="Search"
									variant="outlined"
									size="small"
									value={filterValue}
									onChange={(event) => {
										setFilterValue(event.target.value);
									}}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<IconButton edge="start">
													<SearchIcon />
												</IconButton>
											</InputAdornment>
										),
									}}
								/>
							</Grid2>
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }} style={{ marginTop: 30 }}>
							{isLoading && <CenteredLazyLoading />}
							{!isLoading && (
								<Box
									sx={{
										maxHeight: "50vh",
										overflowY: "auto",
										marginTop: (theme) => theme.spacing(2),
									}}
								>
									{filteredRollsets.map((rollset) => (
										<RollsetCard
											key={rollset.RollsetId}
											rollset={rollset}
											isSelected={selectedRollsetId === rollset.RollsetId}
											onClick={() => {
												setSelectedRollsetId(rollset.RollsetId);
												setValue("RollsetId", rollset.RollsetId);
											}}
										/>
									))}
									{filteredRollsets.length === 0 && !isLoading && (
										<Typography
											variant="body2"
											color="textSecondary"
											style={{ textAlign: "center", marginTop: 20 }}
										>
											No rollsets found
										</Typography>
									)}
								</Box>
							)}
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "15px 30px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="outlined"
									color="primary"
									onClick={() => {
										setShowNewRollsetDialog(true);
									}}
									data-test-id="new-rollset-button"
								>
									New
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3 }} />
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									disabled={selectedRollsetId == null}
									variant="contained"
									color="primary"
									type="submit"
									data-test-id="save-rollset-button"
								>
									Save
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ConfirmationDialogRaw
				title="CONFIRM SELECTION"
				message="This rollset is attached to another carrier. If you continue, that carrier will be disassembled and enter the 'Finished' state."
				open={showConfirmationDialog}
				onClose={(value?: string) => {
					if (value == "OK") {
						onChange(selectedRollsetId);
						onHide(true);
					}
					setShowConfirmationDialog(false);
				}}
			/>

			<NewRollsetDialog
				show={showNewRollsetDialog}
				onHide={(newRollsetId?: number) => {
					setShowNewRollsetDialog(false);
					if (newRollsetId) {
						setSelectedRollsetId(newRollsetId);
						setValue("RollsetId", newRollsetId);
						setNotificationMessage("Rollset created successfully");
						onNewRollsetSaved(newRollsetId);
					}
				}}
				sectionId={sectionId}
				standId={standId}
				standName={standName}
				activeLetters={getActiveLetters()}
			/>
		</>
	);
};
